import React, { useState, useEffect } from "react";
import { Button } from "../../components/adminDashboard/ResultsComponents";
import { FiEdit3, FiPlus, FiTrash2, FiSave } from "react-icons/fi";
import { userRequest } from "../../utils/requestMethods";
import { useStateContext } from "../../contexts/ContextProvider";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";

export default function AddResultMetrics() {
  const { school, refreshSchool } = useStateContext();
  const history = useHistory();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [editSection, setEditSection] = useState(null);
  const [editData, setEditData] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Initialize settings from existing school data or use defaults
  const [settings, setSettings] = useState(() => {
    if (school?.settings) {
      return {
        assessment: {
          assessment_system: school.settings.assessment?.assessment_system || [
            {
              level_of_education: "primary",
              is_default: true,
              ca: {
                cas: [
                  {
                    max_score: 10,
                    title: "CA 1",
                    description: "Continuous Assessment 1"
                  }
                ],
                max_score: 40
              },
              exam: {
                max_score: 60
              }
            }
          ]
        },
        grading: {
          grading_system: school.settings.grading?.grading_system || [
            {
              level_of_education: "primary",
              grades: [
                { grade: "A*", min_score: 90, max_score: 100 },
                { grade: "A", min_score: 80, max_score: 89 },
                { grade: "B", min_score: 70, max_score: 79 },
                { grade: "C", min_score: 60, max_score: 69 },
                { grade: "D", min_score: 50, max_score: 59 },
                { grade: "E", min_score: 40, max_score: 49 },
                { grade: "F", min_score: 30, max_score: 39 },
                { grade: "G", min_score: 20, max_score: 29 }
              ],
              is_default: true
            }
          ]
        },
        remark: school.settings.remark || {
          teacher_remark: true,
          overall_comment: true,
          skills: ["Leadership", "Communication", "Teamwork"],
          ratings: ["Excellent", "Good", "Fair", "Poor"],
          remarks: ["Great performance", "Good effort", "Needs improvement"]
        },
        result: school.settings.result || {
          teacher_preview: true,
          admin_approval: false,
          parent_payment_threshold: 50,
          is_result_released: true
        },
        term_terminology: school.settings.term_terminology || "Term",
        cumulative_result: school.settings.cumulative_result ?? false,
        use_custom_position: school.settings.use_custom_position ?? false
      };
    } else {
      return {
        assessment: {
          assessment_system: [
            {
              level_of_education: "primary",
              is_default: true,
              ca: {
                cas: [
                  {
                    max_score: 10,
                    title: "CA 1",
                    description: "Continuous Assessment 1"
                  }
                ],
                max_score: 40
              },
              exam: {
                max_score: 60
              }
            }
          ]
        },
        grading: {
          grading_system: [
            {
              level_of_education: "primary",
              grades: [
                { grade: "A*", min_score: 90, max_score: 100 },
                { grade: "A", min_score: 80, max_score: 89 },
                { grade: "B", min_score: 70, max_score: 79 },
                { grade: "C", min_score: 60, max_score: 69 },
                { grade: "D", min_score: 50, max_score: 59 },
                { grade: "E", min_score: 40, max_score: 49 },
                { grade: "F", min_score: 30, max_score: 39 },
                { grade: "G", min_score: 20, max_score: 29 }
              ],
              is_default: true
            }
          ]
        },
        remark: {
          teacher_remark: true,
          overall_comment: true,
          skills: ["Leadership", "Communication", "Teamwork"],
          ratings: ["Excellent", "Good", "Fair", "Poor"],
          remarks: ["Great performance", "Good effort", "Needs improvement"]
        },
        result: {
          teacher_preview: true,
          admin_approval: false,
          parent_payment_threshold: 50,
          is_result_released: true
        },
        term_terminology: "Term",
        cumulative_result: false,
        use_custom_position: false
      };
    }
  });

  // Update settings when school data changes
  useEffect(() => {
    if (school?.settings) {
      setSettings({
        assessment: {
          assessment_system: school.settings.assessment?.assessment_system || [
            {
              level_of_education: "primary",
              is_default: true,
              ca: {
                cas: [
                  {
                    max_score: 10,
                    title: "CA 1",
                    description: "Continuous Assessment 1"
                  }
                ],
                max_score: 40
              },
              exam: {
                max_score: 60
              }
            }
          ]
        },
        grading: {
          grading_system: school.settings.grading?.grading_system || [
            {
              level_of_education: "primary",
              grades: [
                { grade: "A*", min_score: 90, max_score: 100 },
                { grade: "A", min_score: 80, max_score: 89 },
                { grade: "B", min_score: 70, max_score: 79 },
                { grade: "C", min_score: 60, max_score: 69 },
                { grade: "D", min_score: 50, max_score: 59 },
                { grade: "E", min_score: 40, max_score: 49 },
                { grade: "F", min_score: 30, max_score: 39 },
                { grade: "G", min_score: 20, max_score: 29 }
              ],
              is_default: true
            }
          ]
        },
        remark: school.settings.remark || {
          teacher_remark: true,
          overall_comment: true,
          skills: ["Leadership", "Communication", "Teamwork"],
          ratings: ["Excellent", "Good", "Fair", "Poor"],
          remarks: ["Great performance", "Good effort", "Needs improvement"]
        },
        result: school.settings.result || {
          teacher_preview: true,
          admin_approval: false,
          parent_payment_threshold: 50,
          is_result_released: true
        },
        term_terminology: school.settings.term_terminology || "Term",
        cumulative_result: school.settings.cumulative_result ?? false,
        use_custom_position: school.settings.use_custom_position ?? false
      });
    }
  }, [school?.settings]);

  useEffect(() => {
    if (location.state?.openSection) {
      handleEdit(location.state.openSection);
    }
  }, []);

  const handleEdit = (section) => {
    setEditSection(section);
    if (section === 'assessment') {
      setEditData(settings?.assessment?.assessment_system ? JSON.parse(JSON.stringify(settings.assessment.assessment_system)) : []);
    } else if (section === 'grading') {
      setEditData(settings?.grading?.grading_system ? JSON.parse(JSON.stringify(settings.grading.grading_system)) : []);
    } else if (section === 'customization') {
      setEditData({ ...(settings?.result ? JSON.parse(JSON.stringify(settings.result)) : {}), cumulative_result: settings?.cumulative_result ?? false, use_custom_position: settings?.use_custom_position ?? false });
    } else if (section === 'remark') {
      setEditData(settings?.remark ? JSON.parse(JSON.stringify(settings.remark)) : {});
    } else if (section === 'terminology') {
      setEditData({ term_terminology: settings?.term_terminology || 'Term' });
    }
  };

  const handleEditSave = (section, newData) => {
    if (section === 'assessment') {
      setSettings(prev => ({
        ...prev,
        assessment: {
          ...prev.assessment,
          assessment_system: newData
        }
      }));
    } else if (section === 'grading') {
      setSettings(prev => ({
        ...prev,
        grading: {
          ...prev.grading,
          grading_system: newData
        }
      }));
    } else if (section === 'customization') {
      const { cumulative_result, use_custom_position, ...resultData } = newData;
      setSettings(prev => ({
        ...prev,
        result: resultData,
        cumulative_result: cumulative_result ?? false,
        use_custom_position: use_custom_position ?? false
      }));
    } else if (section === 'remark') {
      setSettings(prev => ({
        ...prev,
        remark: newData
      }));
    } else if (section === 'terminology') {
      setSettings(prev => ({
        ...prev,
        term_terminology: newData.term_terminology
      }));
    }
    setEditSection(null);
    setEditData(null);
  };

  // Helper function to remove _id fields from nested objects
  const removeIdsFromSettings = (settingsObj) => {
    const cleanSettings = JSON.parse(JSON.stringify(settingsObj));
    
    // Remove _id from assessment_system
    if (cleanSettings.assessment?.assessment_system) {
      cleanSettings.assessment.assessment_system = cleanSettings.assessment.assessment_system.map(system => {
        const cleanSystem = { ...system };
        
        // Remove _id from CA objects
        if (cleanSystem.ca?.cas) {
          cleanSystem.ca.cas = cleanSystem.ca.cas.map(ca => {
            const { _id, ...cleanCA } = ca;
            return cleanCA;
          });
        }
        
        // Remove _id from CA object itself
        if (cleanSystem.ca?._id) {
          const { _id, ...cleanCA } = cleanSystem.ca;
          cleanSystem.ca = cleanCA;
        }
        
        return cleanSystem;
      });
    }
    
    // Remove _id from grading_system
    if (cleanSettings.grading?.grading_system) {
      cleanSettings.grading.grading_system = cleanSettings.grading.grading_system.map(system => {
        const { _id, ...cleanSystem } = system;
        return cleanSystem;
      });
    }
    
    return cleanSettings;
  };

  const submitHandler = async () => {
    setLoading(true);
    try {
      // Determine if we're updating existing settings or creating new ones
      const hasExistingSettings = school?.settings && Object.keys(school.settings).length > 0;
      
      // Clean the settings by removing _id fields before sending
      const cleanSettings = removeIdsFromSettings(settings);
      
      if (hasExistingSettings) {
        // Update existing settings
        await userRequest.post(`/schools/${school._id}/settings`, cleanSettings);
        setSuccess("Settings updated successfully!");
        toast.success("Result metrics updated successfully!");
        setIsUpdating(true);
      } else {
        // Create new settings
        await userRequest.post(`/schools/${school._id}/settings`, cleanSettings);
        setSuccess("Settings saved successfully!");
        toast.success("Result metrics configured successfully!");
      }
      refreshSchool();
      setTimeout(() => {
        history.push("/result-management");
      }, 2000);
    } catch (error) {
      console.error(error);
      setError(error?.response?.data || "Something went wrong. Please try again.");
      toast.error("Failed to save settings");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl dark:bg-secondary-dark-bg min-h-screen">
      <div className="flex flex-wrap gap-4 justify-between items-center">
        <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 dark:text-gray-200">
          {school?.settings && Object.keys(school.settings).length > 0 ? 'Update Result Metrics' : 'Configure Result Metrics'}
        </h1>
        <Button onClick={submitHandler} disabled={loading}>
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              {school?.settings && Object.keys(school.settings).length > 0 ? 'Updating...' : 'Saving...'}
            </>
          ) : (
            <>
              <FiSave className="mr-2" />
              {school?.settings && Object.keys(school.settings).length > 0 ? 'Update Settings' : 'Save and Apply'}
            </>
          )}
        </Button>
      </div>

      {/* Update Mode Indicator */}
      {school?.settings && Object.keys(school.settings).length > 0 && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-full">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-medium text-blue-800">Update Mode</h3>
              <p className="text-sm text-blue-600">
                You are updating existing result metrics. Changes will modify your current configuration.
              </p>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="p-4 my-4 text-sm text-white rounded-lg bg-red-500" role="alert">
          <span className="font-medium">Error!</span> {error}
        </div>
      )}
      
      {success && (
        <div className="p-4 my-4 text-sm text-white rounded-lg bg-green-500" role="alert">
          <span className="font-medium">Success!</span> {success}
        </div>
      )}

      {/* Assessment Systems Section */}
      <div className="mt-10 pt-10 border-t flex flex-wrap justify-between items-center gap-2">
        <div className="w-full">
          <div className="flex justify-between gap-2 items-center">
            <p className="text-lg font-semibold">Assessment Systems</p>
            <button 
              className="text-blue-600 hover:text-blue-800 flex gap-2 items-center px-3 py-1 rounded-lg hover:bg-blue-50 transition-colors" 
              onClick={() => handleEdit('assessment')}
            >
              <FiEdit3/> Edit
            </button>
          </div>

          {settings?.assessment?.assessment_system && Array.isArray(settings.assessment.assessment_system) && settings.assessment.assessment_system.length > 0 ? (
            <div className="mt-6 space-y-4">
              {settings.assessment.assessment_system.map((system, idx) => (
                <div key={idx} className="bg-blue-50 py-4 px-6 rounded-xl border border-blue-200">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-semibold text-blue-800 capitalize">
                      {system.level_of_education} Assessment System
                      {system.is_default && <span className="ml-2 text-sm text-green-600">(Default)</span>}
                    </h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* CA Section */}
                    <div className="bg-white/60 backdrop-blur-sm rounded-lg p-4 border border-blue-300">
                      <h4 className="font-medium text-blue-700 mb-3">Continuous Assessment</h4>
                      <div className="mb-3">
                        <p className="text-sm text-blue-600">Total CA Weight</p>
                        <p className="font-bold text-lg text-blue-900">{system.ca?.max_score || 0}%</p>
                      </div>
                      <div className="space-y-2">
                        {system.ca?.cas?.map((ca, caIdx) => (
                          <div key={caIdx} className="flex justify-between items-center text-sm">
                            <span className="text-blue-700">{ca.title || `CA ${caIdx + 1}`}</span>
                            <span className="font-medium text-blue-800">{ca.max_score}%</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Exam Section */}
                    <div className="bg-white/60 backdrop-blur-sm rounded-lg p-4 border border-green-300">
                      <h4 className="font-medium text-green-700 mb-3">Examination</h4>
                      <div>
                        <p className="text-sm text-green-600">Exam Weight</p>
                        <p className="font-bold text-lg text-green-900">{system.exam?.max_score || 0}%</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-6 text-center py-8 text-gray-500">
              <p>No assessment systems configured yet.</p>
            </div>
          )}
        </div>
      </div>

      {/* Grading System Section */}
      <div className="mt-10 pt-10 border-t flex flex-wrap justify-between items-center gap-2">
        <div className="w-full">
          <div className="flex justify-between gap-2 items-center">
            <p className="text-lg font-semibold">Grading System</p>
            <button 
              className="text-blue-600 hover:text-blue-800 flex gap-2 items-center px-3 py-1 rounded-lg hover:bg-blue-50 transition-colors" 
              onClick={() => handleEdit('grading')}
            >
              <FiEdit3/> Edit
            </button>
          </div>

          <div className="bg-purple-50 mt-6 py-4 px-6 flex justify-between gap-3 rounded-xl border border-purple-200">
            {Array.isArray(settings?.grading?.grading_system)
              ? settings.grading.grading_system.map((system, idx) => (
                  <div key={system.level_of_education || idx} className="max-w-[200px] w-full">
                    <p className="text-purple-600 font-semibold mb-2 capitalize">
                      {system.level_of_education} Grading
                    </p>
                    {system.grades.map((grade, gidx) => (
                      <div key={grade.grade || gidx} className="mb-1 text-sm">
                        <span className="font-semibold text-purple-800">{grade.grade}:</span> 
                        <span className="text-purple-700"> {grade.min_score} - {grade.max_score}</span>
                      </div>
                    ))}
                    {system.is_default && (
                      <span className="text-xs text-green-600 font-medium">(Default)</span>
                    )}
                  </div>
                ))
              : <span className="text-gray-500">No grading system configured.</span>}
          </div>
        </div>
      </div>

      {/* Remarks Configuration Section */}
      <div className="mt-10 pt-10 border-t flex flex-wrap justify-between items-center gap-2">
        <div className="w-full">
          <div className="flex justify-between gap-2 items-center">
            <p className="text-lg font-semibold">Remarks Configuration</p>
            <button 
              className="text-blue-600 hover:text-blue-800 flex gap-2 items-center px-3 py-1 rounded-lg hover:bg-blue-50 transition-colors" 
              onClick={() => handleEdit('remark')}
            >
              <FiEdit3/> Edit
            </button>
          </div>

          <div className="bg-orange-50 mt-6 py-4 px-6 rounded-xl border border-orange-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="font-semibold text-orange-800 mb-2">Settings</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-orange-700">Teacher Remark:</span>
                    <span className="font-medium text-orange-800">
                      {settings?.remark?.teacher_remark ? "Enabled" : "Disabled"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-orange-700">Overall Comment:</span>
                    <span className="font-medium text-orange-800">
                      {settings?.remark?.overall_comment ? "Enabled" : "Disabled"}
                    </span>
                  </div>
                </div>
              </div>
              <div>
                <p className="font-semibold text-orange-800 mb-2">Available Options</p>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-orange-700">Skills:</span>
                    <span className="ml-2 text-orange-800">
                      {settings?.remark?.skills?.join(", ") || "None"}
                    </span>
                  </div>
                  <div>
                    <span className="text-orange-700">Ratings:</span>
                    <span className="ml-2 text-orange-800">
                      {settings?.remark?.ratings?.join(", ") || "None"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Term Terminology Section */}
      <div className="mt-10 pt-10 border-t flex flex-wrap justify-between items-center gap-2">
        <div className="w-full">
          <div className="flex justify-between gap-2 items-center">
            <p className="text-lg font-semibold">Term Terminology</p>
            <button 
              className="text-blue-600 hover:text-blue-800 flex gap-2 items-center px-3 py-1 rounded-lg hover:bg-blue-50 transition-colors" 
              onClick={() => handleEdit('terminology')}
            >
              <FiEdit3/> Edit
            </button>
          </div>

          <div className="bg-indigo-50 mt-6 py-4 px-6 rounded-xl border border-indigo-200">
            <div className="flex items-center gap-4">
              <div className="bg-white p-4 rounded-lg border border-indigo-300">
                <p className="text-sm text-indigo-600 mb-1">Current Terminology</p>
                <p className="text-xl font-bold text-indigo-900">
                  {settings?.term_terminology || 'Term'}
                </p>
              </div>
              <div className="text-sm text-indigo-700">
                <p className="font-medium mb-1">This will be used throughout the system for:</p>
                <ul className="space-y-1 text-indigo-600">
                  <li>• Result management</li>
                  <li>• Academic calendars</li>
                  <li>• Student reports</li>
                  <li>• Teacher interfaces</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* General Customization Section */}
      <div className="mt-10 pt-10 border-t">
        <div className="flex justify-between gap-2 items-center">
          <p className="text-lg font-semibold">General Customization</p>
          <button 
            className="text-blue-600 hover:text-blue-800 flex gap-2 items-center px-3 py-1 rounded-lg hover:bg-blue-50 transition-colors" 
            onClick={() => handleEdit('customization')}
          >
            <FiEdit3/> Edit
          </button>
        </div>

        <div className="bg-gray-50 mt-6 py-4 px-6 rounded-xl border border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="font-medium text-gray-700">Enable preview option for teachers</span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  settings?.result?.teacher_preview 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {settings?.result?.teacher_preview ? "Yes" : "No"}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="font-medium text-gray-700">Admin must review all results</span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  settings?.result?.admin_approval 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {settings?.result?.admin_approval ? "Yes" : "No"}
                </span>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="font-medium text-gray-700">Parent Payment Threshold</span>
                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                  {settings?.result?.parent_payment_threshold || 0}%
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="font-medium text-gray-700">Results Released</span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  settings?.result?.is_result_released
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {settings?.result?.is_result_released ? "Yes" : "No"}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="font-medium text-gray-700">Cumulative Result</span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  settings?.cumulative_result
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {settings?.cumulative_result ? "Yes" : "No"}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="font-medium text-gray-700">Custom Position</span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  settings?.use_custom_position
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {settings?.use_custom_position ? "Yes" : "No"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modals */}
      {editSection === 'assessment' && (
        <AssessmentModal 
          editData={editData} 
          setEditData={setEditData} 
          onSave={(data) => handleEditSave('assessment', data)} 
          onCancel={() => setEditSection(null)} 
        />
      )}

      {editSection === 'grading' && (
        <GradingModal 
          editData={editData} 
          setEditData={setEditData} 
          onSave={(data) => handleEditSave('grading', data)} 
          onCancel={() => setEditSection(null)} 
        />
      )}

      {editSection === 'customization' && (
        <CustomizationModal 
          editData={editData} 
          setEditData={setEditData} 
          onSave={(data) => handleEditSave('customization', data)} 
          onCancel={() => setEditSection(null)} 
        />
      )}

      {editSection === 'remark' && (
        <RemarkModal 
          editData={editData} 
          setEditData={setEditData} 
          onSave={(data) => handleEditSave('remark', data)} 
          onCancel={() => setEditSection(null)} 
        />
      )}

      {editSection === 'terminology' && (
        <TerminologyModal 
          editData={editData} 
          setEditData={setEditData} 
          onSave={(data) => handleEditSave('terminology', data)} 
          onCancel={() => setEditSection(null)} 
        />
      )}
    </div>
  );
}

// Assessment System Modal Component
function AssessmentModal({ editData, setEditData, onSave, onCancel }) {
  const [selectedSystem, setSelectedSystem] = useState(0);
  const levels = ['kindergarten', 'nursery', 'primary', 'secondary'];

  const addAssessmentSystem = (level) => {
    if (!editData.some(s => s.level_of_education === level)) {
      const newSystem = {
        level_of_education: level,
        is_default: editData.length === 0,
        ca: {
          cas: [
            {
              max_score: 10,
              title: "CA 1",
              description: "Continuous Assessment 1"
            }
          ],
          max_score: 40
        },
        exam: {
          max_score: 60
        }
      };
      setEditData([...editData, newSystem]);
    }
  };

  const removeAssessmentSystem = (index) => {
    setEditData(editData.filter((_, i) => i !== index));
    setSelectedSystem(Math.max(0, selectedSystem - 1));
  };

  const setDefault = (index) => {
    setEditData(editData.map((s, i) => ({ ...s, is_default: i === index })));
  };

  const addCA = (systemIndex) => {
    const newData = [...editData];
    const caCount = newData[systemIndex].ca.cas.length;
    newData[systemIndex].ca.cas.push({
      max_score: 0,
      title: `CA ${caCount + 1}`,
      description: `Continuous Assessment ${caCount + 1}`
    });
    setEditData(newData);
  };

  const removeCA = (systemIndex, caIndex) => {
    const newData = [...editData];
    newData[systemIndex].ca.cas = newData[systemIndex].ca.cas.filter((_, i) => i !== caIndex);
    setEditData(newData);
  };

  const updateCA = (systemIndex, caIndex, field, value) => {
    const newData = [...editData];
    newData[systemIndex].ca.cas[caIndex][field] = value;
    setEditData(newData);
  };

  const updateCAWeight = (systemIndex, weight) => {
    const newData = [...editData];
    newData[systemIndex].ca.max_score = weight;
    newData[systemIndex].exam.max_score = 100 - weight;
    setEditData(newData);
  };

  // Validation function to check if total CA scores exceed max_score
  const getValidationError = (systemIndex) => {
    const system = editData[systemIndex];
    if (!system) return null;
    
    const totalCAScores = system.ca.cas.reduce((sum, ca) => sum + (ca.max_score || 0), 0);
    const maxScore = system.ca.max_score;
    
    if (totalCAScores > maxScore) {
      return `Total CA scores (${totalCAScores}%) exceed maximum allowed (${maxScore}%)`;
    }
    
    return null;
  };

  // Check if any system has validation errors
  const hasValidationErrors = () => {
    return editData.some((_, index) => getValidationError(index) !== null);
  };

  return (
    <div className="fixed top-0 left-0 w-full h-screen bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-3 z-50">
      <div className="bg-white max-w-5xl w-full rounded-2xl p-6 relative max-h-[90vh] overflow-y-auto">
        <button 
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-2xl" 
          onClick={onCancel}
        >
          ×
        </button>
        
        <h2 className="text-xl font-bold mb-6 text-gray-800">Configure Assessment Systems</h2>

        {/* Add Education Level Systems */}
        <div className="flex gap-2 mb-6">
          {levels.map(level => (
            <button
              key={level}
              type="button"
              onClick={() => addAssessmentSystem(level)}
              disabled={editData.some(s => s.level_of_education === level)}
              className={`px-4 py-2 rounded-lg border capitalize transition-colors ${
                editData.some(s => s.level_of_education === level)
                  ? 'bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100'
              }`}
            >
              Add {level} System
            </button>
          ))}
        </div>

        {editData.length > 0 && (
          <>
            {/* System Selection Tabs */}
            <div className="flex gap-2 mb-6 flex-wrap">
              {editData.map((system, idx) => (
                <button
                  key={system.level_of_education}
                  type="button"
                  onClick={() => setSelectedSystem(idx)}
                  className={`px-4 py-2 rounded-lg border capitalize transition-colors ${
                    selectedSystem === idx
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {system.level_of_education}
                  {system.is_default && <span className="ml-2 text-xs">(Default)</span>}
                </button>
              ))}
            </div>

            {/* System Management */}
            <div className="flex gap-2 mb-6 flex-wrap">
              {editData.length > 1 && editData.map((system, idx) => (
                <button
                  key={`remove-${system.level_of_education}`}
                  type="button"
                  onClick={() => removeAssessmentSystem(idx)}
                  className="px-3 py-1 text-sm text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition-colors"
                >
                  Remove {system.level_of_education}
                </button>
              ))}
              {editData.length > 1 && editData.map((system, idx) => (
                <button
                  key={`default-${system.level_of_education}`}
                  type="button"
                  onClick={() => setDefault(idx)}
                  disabled={system.is_default}
                  className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                    system.is_default
                      ? 'bg-green-100 text-green-700 cursor-not-allowed'
                      : 'bg-green-50 text-green-600 border border-green-300 hover:bg-green-100'
                  }`}
                >
                  Set {system.level_of_education} as Default
                </button>
              ))}
            </div>

            {/* Current System Configuration */}
            {editData[selectedSystem] && (
              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="font-semibold text-gray-700 mb-6 capitalize">
                  {editData[selectedSystem].level_of_education} Assessment System
                </h3>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* CA Configuration */}
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <h4 className="font-medium text-gray-700">Continuous Assessment</h4>
                    </div>
                    
                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-700">Total CA Weight (%)</label>
                      <input 
                        type="number" 
                        min="0" 
                        max="100" 
                        value={editData[selectedSystem].ca.max_score} 
                        onChange={e => updateCAWeight(selectedSystem, Number(e.target.value))}
                        className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" 
                      />
                      <p className="text-sm text-gray-500 mt-1">
                        Remaining {100 - editData[selectedSystem].ca.max_score}% will be allocated to exams
                      </p>
                    </div>

                    <div>
                      <div className="flex justify-between items-center mb-3">
                        <h5 className="font-medium text-gray-700">CA Components</h5>
                        <button 
                          type="button" 
                          onClick={() => addCA(selectedSystem)}
                          className="flex items-center gap-2 text-blue-600 hover:text-blue-800 px-3 py-1 rounded-lg hover:bg-blue-50 transition-colors text-sm"
                        >
                          <FiPlus className="w-4 h-4" />
                          Add CA
                        </button>
                      </div>

                      <div className="space-y-3">
                        {editData[selectedSystem].ca.cas.map((ca, idx) => (
                          <div key={idx} className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                            <div className="flex gap-3 items-center mb-3">
                              <input
                                type="text"
                                value={ca.title}
                                onChange={e => updateCA(selectedSystem, idx, 'title', e.target.value)}
                                placeholder="CA Title"
                                className="flex-1 border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                              />
                              <input
                                type="number"
                                min="0"
                                max={editData[selectedSystem].ca.max_score}
                                value={ca.max_score}
                                onChange={e => updateCA(selectedSystem, idx, 'max_score', Number(e.target.value))}
                                placeholder="Score"
                                className="w-20 border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                              />
                              <button
                                type="button"
                                onClick={() => removeCA(selectedSystem, idx)}
                                className="text-red-500 hover:text-red-700 p-2 hover:bg-red-50 rounded-lg transition-colors"
                              >
                                <FiTrash2 className="w-4 h-4" />
                              </button>
                            </div>
                            <div>
                              <input
                                type="text"
                                value={ca.description || ''}
                                onChange={e => updateCA(selectedSystem, idx, 'description', e.target.value)}
                                placeholder="Optional description (e.g., 'First term assessment', 'Practical evaluation')"
                                className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                              />
                            </div>
                          </div>
                        ))}
                        
                        {/* Validation Error Display */}
                        {getValidationError(selectedSystem) && (
                          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <div className="flex items-center gap-2">
                              <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <span className="text-sm text-red-700 font-medium">
                                {getValidationError(selectedSystem)}
                              </span>
                            </div>
                          </div>
                        )}
                        
                        {/* Total CA Score Display */}
                        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-blue-700 font-medium">Total CA Scores:</span>
                            <span className="text-sm text-blue-900 font-bold">
                              {editData[selectedSystem].ca.cas.reduce((sum, ca) => sum + (ca.max_score || 0), 0)}%
                            </span>
                          </div>
                          <div className="flex justify-between items-center mt-1">
                            <span className="text-xs text-blue-600">Maximum Allowed:</span>
                            <span className="text-xs text-blue-800">
                              {editData[selectedSystem].ca.max_score}%
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Exam Configuration */}
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <h4 className="font-medium text-gray-700">Examination</h4>
                    </div>
                    
                    <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                      <label className="block mb-2 text-sm font-medium text-green-700">Exam Weight</label>
                      <div className="text-2xl font-bold text-green-900">
                        {editData[selectedSystem].exam.max_score}%
                      </div>
                      <p className="text-sm text-green-600 mt-1">
                        Automatically calculated based on CA weight
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        <div className="flex gap-3 justify-end mt-6">
          <button 
            type="button" 
            onClick={onCancel}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button 
            type="button" 
            onClick={() => onSave(editData)}
            disabled={hasValidationErrors()}
            className={`px-6 py-2 rounded-lg transition-colors ${
              hasValidationErrors()
                ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {hasValidationErrors() ? 'Fix Errors to Save' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Terminology Modal Component
function TerminologyModal({ editData, setEditData, onSave, onCancel }) {
  const commonTerms = ['Term', 'Semester', 'Quarter', 'Period', 'Session', 'Phase'];
  
  const handleCustomTerm = (value) => {
    setEditData({ term_terminology: value });
  };

  return (
    <div className="fixed top-0 left-0 w-full h-screen bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-3 z-50">
      <div className="bg-white max-w-2xl w-full rounded-2xl p-6 relative">
        <button 
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-2xl" 
          onClick={onCancel}
        >
          ×
        </button>
        
        <h2 className="text-xl font-bold mb-6 text-gray-800">Configure Term Terminology</h2>
        
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Choose or enter custom terminology for academic periods
            </label>
            
            {/* Common Terms */}
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-600 mb-2">Common Options:</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {commonTerms.map((term) => (
                  <button
                    key={term}
                    type="button"
                    onClick={() => handleCustomTerm(term)}
                    className={`px-4 py-2 rounded-lg border text-sm transition-colors ${
                      editData.term_terminology === term
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-gray-50 border-gray-300 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {term}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Or enter custom terminology:
              </label>
              <input
                type="text"
                value={editData.term_terminology}
                onChange={(e) => handleCustomTerm(e.target.value)}
                placeholder="e.g., Trimester, Module, Cycle"
                className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-sm text-gray-500 mt-1">
                This will replace "Term" throughout the system (e.g., "1st Term" becomes "1st {editData.term_terminology}")
              </p>
            </div>
          </div>

          {/* Preview */}
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h4 className="font-medium text-blue-800 mb-2">Preview:</h4>
            <div className="text-sm text-blue-700 space-y-1">
              <p>• 1st {editData.term_terminology} Results</p>
              <p>• {editData.term_terminology} 1 Assessment</p>
              <p>• End of {editData.term_terminology} Report</p>
              <p>• {editData.term_terminology} Schedule</p>
            </div>
          </div>
        </div>

        <div className="flex gap-3 justify-end mt-6">
          <button 
            type="button" 
            onClick={onCancel}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button 
            type="button" 
            onClick={() => onSave(editData)}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

// Grading Modal Component
function GradingModal({ editData, setEditData, onSave, onCancel }) {
  const [selectedLevel, setSelectedLevel] = useState(0);
  const levels = ['primary', 'secondary'];

  const addGradingSystem = (level) => {
    if (!editData.some(g => g.level_of_education === level)) {
      let defaultGrades;
      
      if (level === 'primary') {
        defaultGrades = [
          {grade: "A*", min_score: 90, max_score: 100},
          {grade: "A", min_score: 80, max_score: 89},
          {grade: "B", min_score: 70, max_score: 79},
          {grade: "C", min_score: 60, max_score: 69},
          {grade: "D", min_score: 50, max_score: 59},
          {grade: "E", min_score: 40, max_score: 49},
          {grade: "F", min_score: 30, max_score: 39},
          {grade: "G", min_score: 20, max_score: 29}
        ];
      } else if (level === 'secondary') {
        defaultGrades = [
          {grade: "A1", min_score: 75, max_score: 100},
          {grade: "B2", min_score: 70, max_score: 74},
          {grade: "B3", min_score: 65, max_score: 69},
          {grade: "C4", min_score: 60, max_score: 64},
          {grade: "C5", min_score: 55, max_score: 59},
          {grade: "C6", min_score: 50, max_score: 54},
          {grade: "D7", min_score: 45, max_score: 49},
          {grade: "D8", min_score: 40, max_score: 44},
          {grade: "F9", min_score: 0, max_score: 39}
        ];
      }
      
      const newSystem = {
        level_of_education: level,
        grades: defaultGrades,
        is_default: editData.length === 0
      };
      setEditData([...editData, newSystem]);
    }
  };

  const setDefault = (index) => {
    setEditData(editData.map((g, i) => ({ ...g, is_default: i === index })));
  };

  const updateGrade = (levelIndex, gradeIndex, field, value) => {
    const newData = [...editData];
    newData[levelIndex].grades[gradeIndex][field] = value;
    setEditData(newData);
  };

  const removeGrade = (levelIndex, gradeIndex) => {
    const newData = [...editData];
    newData[levelIndex].grades = newData[levelIndex].grades.filter((_, i) => i !== gradeIndex);
    setEditData(newData);
  };

  const addGrade = () => {
    const newData = [...editData];
    newData[selectedLevel].grades.push({ grade: '', min_score: 0, max_score: 0 });
    setEditData(newData);
  };

  return (
    <div className="fixed top-0 left-0 w-full h-screen bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-3 z-50">
      <div className="bg-white max-w-4xl w-full rounded-2xl p-6 relative max-h-[90vh] overflow-y-auto">
        <button 
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-2xl" 
          onClick={onCancel}
        >
          ×
        </button>
        
        <h2 className="text-xl font-bold mb-6 text-gray-800">Configure Grading System</h2>

        <div className="flex gap-2 mb-6">
          {levels.map(level => (
            <button
              key={level}
              type="button"
              onClick={() => addGradingSystem(level)}
              disabled={editData.some(g => g.level_of_education === level)}
              className={`px-4 py-2 rounded-lg border capitalize transition-colors ${
                editData.some(g => g.level_of_education === level)
                  ? 'bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100'
              }`}
            >
              Add {level} Grading
            </button>
          ))}
        </div>

        {editData.length > 0 && (
          <>
            <div className="flex gap-2 mb-6 flex-wrap">
              {editData.map((system, idx) => (
                <button
                  key={system.level_of_education}
                  type="button"
                  onClick={() => setSelectedLevel(idx)}
                  className={`px-4 py-2 rounded-lg border capitalize transition-colors ${
                    selectedLevel === idx
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {system.level_of_education}
                  {system.is_default && <span className="ml-2 text-xs">(Default)</span>}
                </button>
              ))}
            </div>

            {editData[selectedLevel] && (
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold text-gray-700 capitalize">
                    {editData[selectedLevel].level_of_education} Grading System
                  </h3>
                  <button
                    type="button"
                    onClick={() => setDefault(selectedLevel)}
                    disabled={editData[selectedLevel].is_default}
                    className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                      editData[selectedLevel].is_default
                        ? 'bg-green-100 text-green-700 cursor-not-allowed'
                        : 'bg-green-50 text-green-600 hover:bg-green-100'
                    }`}
                  >
                    Set as Default
                  </button>
                </div>

                <div className="space-y-3">
                  {editData[selectedLevel].grades.map((grade, index) => (
                    <div key={index} className="flex gap-2 items-center p-3 border border-gray-200 rounded-lg bg-gray-50">
                      <input
                        type="text"
                        value={grade.grade}
                        onChange={e => updateGrade(selectedLevel, index, 'grade', e.target.value)}
                        placeholder="Grade"
                        className="w-16 border border-gray-300 rounded p-2 text-center font-semibold focus:ring-2 focus:ring-blue-500"
                      />
                      <input
                        type="number"
                        value={grade.min_score}
                        onChange={e => updateGrade(selectedLevel, index, 'min_score', Number(e.target.value))}
                        placeholder="Min"
                        className="w-20 border border-gray-300 rounded p-2 focus:ring-2 focus:ring-blue-500"
                      />
                      <span className="text-gray-500 font-medium">-</span>
                      <input
                        type="number"
                        value={grade.max_score}
                        onChange={e => updateGrade(selectedLevel, index, 'max_score', Number(e.target.value))}
                        placeholder="Max"
                        className="w-20 border border-gray-300 rounded p-2 focus:ring-2 focus:ring-blue-500"
                      />
                      <button
                        type="button"
                        onClick={() => removeGrade(selectedLevel, index)}
                        className="ml-auto p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                        title="Remove grade"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  ))}
                  
                  <button
                    type="button"
                    onClick={addGrade}
                    className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-colors flex items-center justify-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add New Grade
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        <div className="flex gap-3 justify-end mt-6">
          <button 
            type="button" 
            onClick={onCancel}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button 
            type="button" 
            onClick={() => onSave(editData)}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

// Customization Modal Component
function CustomizationModal({ editData, setEditData, onSave, onCancel }) {
  return (
    <div className="fixed top-0 left-0 w-full h-screen bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-3 z-50">
      <div className="bg-white max-w-2xl w-full rounded-2xl p-6 relative">
        <button 
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-2xl" 
          onClick={onCancel}
        >
          ×
        </button>
        
        <h2 className="text-xl font-bold mb-6 text-gray-800">General Customization</h2>
        
        <div className="space-y-6">
          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div>
              <label className="font-semibold text-gray-700">Enable preview option for teachers</label>
              <p className="text-sm text-gray-500">Allow teachers to preview results before publishing</p>
            </div>
            <input 
              type="checkbox" 
              checked={!!editData.teacher_preview} 
              onChange={e => setEditData(d => ({...d, teacher_preview: e.target.checked}))}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
          </div>

          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div>
              <label className="font-semibold text-gray-700">Admin must review all results</label>
              <p className="text-sm text-gray-500">Require admin approval before results are published</p>
            </div>
            <input 
              type="checkbox" 
              checked={!!editData.admin_approval} 
              onChange={e => setEditData(d => ({...d, admin_approval: e.target.checked}))}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
          </div>

          <div className="p-4 border border-gray-200 rounded-lg">
            <label className="block font-semibold text-gray-700 mb-2">Parent Payment Threshold (%)</label>
            <input 
              type="number" 
              min="0" 
              max="100" 
              value={editData.parent_payment_threshold || 0} 
              onChange={e => setEditData(d => ({...d, parent_payment_threshold: Number(e.target.value)}))}
              className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-sm text-gray-500 mt-1">Minimum payment percentage required to view results</p>
          </div>

          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div>
              <label className="font-semibold text-gray-700">Results Released</label>
              <p className="text-sm text-gray-500">Allow students and parents to view results</p>
            </div>
            <input
              type="checkbox"
              checked={!!editData.is_result_released}
              onChange={e => setEditData(d => ({...d, is_result_released: e.target.checked}))}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
          </div>

          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div>
              <label className="font-semibold text-gray-700">Cumulative Result</label>
              <p className="text-sm text-gray-500">Include all previous terms in student 3rd term result reports</p>
            </div>
            <input
              type="checkbox"
              checked={!!editData.cumulative_result}
              onChange={e => setEditData(d => ({...d, cumulative_result: e.target.checked}))}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
          </div>

          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div>
              <label className="font-semibold text-gray-700">Custom Position</label>
              <p className="text-sm text-gray-500">Allow a custom position to be typed in, e.g. "First in Upper Grade 1", instead of/alongside the automatic rank</p>
            </div>
            <input
              type="checkbox"
              checked={!!editData.use_custom_position}
              onChange={e => setEditData(d => ({...d, use_custom_position: e.target.checked}))}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="flex gap-3 justify-end mt-6">
          <button 
            type="button" 
            onClick={onCancel}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button 
            type="button" 
            onClick={() => onSave(editData)}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

// Remark Modal Component
function RemarkModal({ editData, setEditData, onSave, onCancel }) {
  const addItem = (field) => {
    setEditData(d => ({
      ...d,
      [field]: [...(d[field] || []), ""]
    }));
  };

  const removeItem = (field, index) => {
    setEditData(d => ({
      ...d,
      [field]: d[field].filter((_, i) => i !== index)
    }));
  };

  const updateItem = (field, index, value) => {
    setEditData(d => ({
      ...d,
      [field]: d[field].map((item, i) => i === index ? value : item)
    }));
  };

  return (
    <div className="fixed top-0 left-0 w-full h-screen bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-3 z-50">
      <div className="bg-white max-w-4xl w-full rounded-2xl p-6 relative max-h-[90vh] overflow-y-auto">
        <button 
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-2xl" 
          onClick={onCancel}
        >
          ×
        </button>
        
        <h2 className="text-xl font-bold mb-6 text-gray-800">Configure Remarks</h2>
        
        <div className="space-y-6">
          {/* Settings */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
              <div>
                <label className="font-semibold text-gray-700">Teacher Remark</label>
                <p className="text-sm text-gray-500">Allow teachers to add remarks</p>
              </div>
              <input 
                type="checkbox" 
                checked={!!editData.teacher_remark} 
                onChange={e => setEditData(d => ({...d, teacher_remark: e.target.checked}))}
                className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
              />
            </div>

            <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
              <div>
                <label className="font-semibold text-gray-700">Overall Comment</label>
                <p className="text-sm text-gray-500">Allow overall comments on results</p>
              </div>
              <input 
                type="checkbox" 
                checked={!!editData.overall_comment} 
                onChange={e => setEditData(d => ({...d, overall_comment: e.target.checked}))}
                className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Skills */}
          <div className="p-4 border border-gray-200 rounded-lg">
            <div className="mb-4">
              <h3 className="font-semibold text-gray-700">Available Skills</h3>
            </div>
            <div className="space-y-2">
              {(editData.skills || []).map((skill, index) => (
                <div key={index} className="flex gap-2 items-center">
                  <input
                    type="text"
                    value={skill}
                    onChange={e => updateItem('skills', index, e.target.value)}
                    placeholder="Enter skill name"
                    className="flex-1 border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => removeItem('skills', index)}
                    className="text-red-500 hover:text-red-700 p-2 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <FiTrash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <button 
              type="button" 
              onClick={() => addItem('skills')}
              className="mt-4 flex items-center gap-2 text-blue-600 hover:text-blue-800 px-3 py-1 rounded-lg hover:bg-blue-50 transition-colors"
            >
              <FiPlus className="w-4 h-4" />
              Add Skill
            </button>
          </div>

          {/* Ratings */}
          <div className="p-4 border border-gray-200 rounded-lg">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-gray-700">Available Ratings</h3>
              <button 
                type="button" 
                onClick={() => addItem('ratings')}
                className="flex items-center gap-2 text-blue-600 hover:text-blue-800 px-3 py-1 rounded-lg hover:bg-blue-50 transition-colors"
              >
                <FiPlus className="w-4 h-4" />
                Add Rating
              </button>
            </div>
            <div className="space-y-2">
              {(editData.ratings || []).map((rating, index) => (
                <div key={index} className="flex gap-2 items-center">
                  <input
                    type="text"
                    value={rating}
                    onChange={e => updateItem('ratings', index, e.target.value)}
                    placeholder="Enter rating name"
                    className="flex-1 border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => removeItem('ratings', index)}
                    className="text-red-500 hover:text-red-700 p-2 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <FiTrash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Remarks */}
          <div className="p-4 border border-gray-200 rounded-lg">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-gray-700">Available Remarks</h3>
              <button 
                type="button" 
                onClick={() => addItem('remarks')}
                className="flex items-center gap-2 text-blue-600 hover:text-blue-800 px-3 py-1 rounded-lg hover:bg-blue-50 transition-colors"
              >
                <FiPlus className="w-4 h-4" />
                Add Remark
              </button>
            </div>
            <div className="space-y-2">
              {(editData.remarks || []).map((remark, index) => (
                <div key={index} className="flex gap-2 items-center">
                  <input
                    type="text"
                    value={remark}
                    onChange={e => updateItem('remarks', index, e.target.value)}
                    placeholder="Enter remark text"
                    className="flex-1 border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => removeItem('remarks', index)}
                    className="text-red-500 hover:text-red-700 p-2 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <FiTrash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex gap-3 justify-end mt-6">
          <button 
            type="button" 
            onClick={onCancel}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button 
            type="button" 
            onClick={() => onSave(editData)}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}
